import moment from 'moment';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useProtectedPageContext } from 'components/ProtectedPageContainer';
import CardDetailsForm, { FlowType } from 'views/Accounts/components/Checkout.Com/CardDetailsForm';
import { SlideOutPanelViews } from 'views/Accounts/types';
import { useNavigate } from 'react-router-dom';
import useMetadata from 'hooks/useMetadata';
import { usePortfolioHoldingsTotals } from 'hooks/usePortfolioHoldingsTotals';
import { selectImage } from 'views/Accounts/components/Checkout.Com/Card';
import EditCardHomePage from 'views/Accounts/components/slideoutTemplates/EditCardHomePage';
import { useSnackbar } from 'notistack';
import { Product } from 'types/productType';
import { usePortalHoldings } from 'views/MyCellar/hooks/usePortalHoldings';
import SlideOutPanel from 'components/SlideOutPanel';
import ProductSlideout from 'components/ProductTemplates/components/ProductSlideout';
import { AddCardIcon, PayPassIcon } from '../../../../assets/icons';
import { PerformanceStoryChart } from '../../../../components/Charts';
import { PerformanceStorySeries } from '../../../../components/Charts/PerformanceStoryChart';
import { AccountSummarySkeleton, ChartSkeleton } from '../../../../components/Skeleton';
import { getPortfolioInfo } from '../../../../helpers';
import { NavigationPath, PortfolioBalance, PortfolioBalanceInfo } from '../../../../types/DomainTypes';
import { buildDisplayText, formatterGbp } from '../../../../utils';
import { getHoldingsVsInvestedChartDatasource } from '../../helpers';
import {
  usePortfolioPerformanceOverTime,
  UsePortfolioPerformanceOverTimeResponse,
} from './hooks/usePortfolioPerformanceOverTime';
import { useReturnValuations, ReturnValuationPerformance } from './hooks/useReturnValuations';
import { ISelectedTabs, ViewKeys } from '../../types';
import { AppContext } from '../../../../context/ContextProvider';
import { useTheme } from '../../../../context/ThemeContext';
import PerformanceMomentum from '../PerformanceMomentum';
import AccountOverview from '../AccountOverview';
import TopBottomPerformers from '../TopBottomPerformers';
import RegionalValuePerformance from '../RegionalValuePerformance';
import PortfolioQuickStats from '../PortfolioQuickStats';

// Theme colors for Account Summary (matching other panes)
const accountSummaryLightTheme = {
  background: '#FFFFFF',
  cardBackground: '#F8FAFC',
  cardHover: '#F1F5F9',
  text: '#000000',
  textSecondary: '#666666',
  border: '#E2E8F0',
  accent: '#1D4854',
  accentLight: 'rgba(29, 72, 84, 0.1)',
  positive: '#059669',
};

const accountSummaryDarkTheme = {
  background: '#0A1628',
  cardBackground: '#0F1E32',
  cardHover: '#162A42',
  text: '#FFFFFF',
  textSecondary: '#8B9CB3',
  border: '#1E3A50',
  accent: '#5eead4',
  accentLight: 'rgba(94, 234, 212, 0.15)',
  positive: '#10B981',
};

type PerformancePeriodKey = '1Y' | '3Y' | '5Y' | 'All';

const periodToMonthsMap: Record<PerformancePeriodKey, number> = {
  '1Y': 12,
  '3Y': 36,
  '5Y': 60,
  All: -1,
};

const seriesColors = ['#28515C', '#FF906D'];
enum DisplayTextKeysEnum {
  CURRENTLY_UNAVAILABLE = 'common:currentlyUnavailable',
  VIEW_PORTFOLIO_BUTTON = 'portfolio:viewPortfolioButton',
  PORTFOLIO_FILTER_TEXT = 'portfolio:filterPortfolioText',
  PORTFOLIO_FILTER_PLACEHOLDER_TEXT = 'portfolio:filterPortfolioPlaceHolderText',
  TEXT_PORTFOLIO_BALANCE = 'texts.portfolioBalance',
  NET_CONTRIBUTIONS_TEXT = 'texts.netContributions',
  CURRENT_HOLDINGS_TEXT = 'texts.currentHoldings',
  TEXT_VIEW_DETAILS = 'texts.viewDetails',
  TEXT_ALL_TIME = 'texts.all_time',
  PORTFOLIO_VALUATION_TOOLTIP = 'texts.portfolioValuation_tooltip',
  ARRANGE_DELIVERY_BUTTON_TEXT = 'arrangeDelivery.buttonText',
  ARRANGE_DELIVERY_TITLE = 'arrangeDelivery.title',
  ARRANGE_DELIVERY_SUBTITLE = 'arrangeDelivery.subTitle',

  WELCOME_TITLE = 'portfolio:welcome_banner.title',
  WELCOME_SUBTITLE = 'portfolio:welcome_banner.subTitle',
  WELCOME_DESCRIPTION = 'portfolio:welcome_banner.description',
  VIEW_SETTINGS = 'portfolio:viewSettings',
  SAVE_CARDS = 'account:payments.savedCards',
}

// enum DisplayTextKeys {
//   SUMMARY_TITLE = 'portfolio:titles.summaryPage',
//   DETAIL_TITLE = 'portfolio:titles.detailsPage',
//   VIEW_PORTFOLIO_BUTTON = 'portfolio:viewPortfolioButton',
//   WELCOME_TITLE = 'portfolio:welcome_banner.title',
//   WELCOME_SUBTITLE = 'portfolio:welcome_banner.subTitle',
//   WELCOME_DESCRIPTION = 'portfolio:welcome_banner.description',
// }

const DisplayTextKeys = {
  ...DisplayTextKeysEnum,
  ...PortfolioBalanceInfo,
};

interface SummaryProps {
  onViewChange: (viewKey: ViewKeys, selectedOptionId: string) => void;
  portfolioBalances: PortfolioBalance[];
  currentPortfolio: string;
  loadingBalances: boolean;
}

const filterPeriod = (
  datasource: UsePortfolioPerformanceOverTimeResponse['portfolioPerformanceOverTime'],
  periodInMonths = 0,
): UsePortfolioPerformanceOverTimeResponse['portfolioPerformanceOverTime'] => {
  if (periodInMonths === -1) return datasource;
  const today = moment();
  const endDate = today.subtract(1, 'months').endOf('month');
  const startDate =
    periodInMonths === 0
      ? moment(`${today.year() - 1}-12-31`)
      : moment(endDate).subtract(periodInMonths, 'months').endOf('month');
  // find date between startDate and endDate inclusive of endDate

  const filteredData = datasource.filter(
    (x) => moment(x.date, 'YYYY-MM-DD').isBetween(startDate, endDate) || moment(x.date, 'YYYY-MM-DD').isSame(endDate),
  );

  return filteredData;
};

export const Summary: FC<SummaryProps> = ({ onViewChange, portfolioBalances, currentPortfolio, loadingBalances }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const accountThemeColors = isDark ? accountSummaryDarkTheme : accountSummaryLightTheme;
  const { managementFeeUpdates14758 } = useFlags();
  const {
    state: {
      settings: { selectedPortfolioId },
    },
    gbpToTargetCurrency,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { closeSlidout, openSlideout } = useProtectedPageContext();
  const [selectedChartPeriod, setSelctedChartPeriod] = useState<PerformancePeriodKey>('5Y');
  const [selectedPortfolioBalance, setSelectedPortfolioBalance] = useState(currentPortfolio);
  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'portfolio:summary', t), [t]);
  // Extract accountHolderId from parent's already-fetched portfolio balances (avoids extra API call)
  const accountHolderId = useMemo(
    () => portfolioBalances.find((p) => p.accountHolderId)?.accountHolderId ?? null,
    [portfolioBalances],
  );
  // Use return valuations API for Performance Story chart
  const {
    returnValuations,
    summary: returnValuationsSummary,
    loading: loadingReturnValuations,
  } = useReturnValuations(selectedPortfolioBalance, accountHolderId);
  // Keep legacy hook as fallback - only fires when return valuations API has no data
  const { portfolioPerformanceOverTime } = usePortfolioPerformanceOverTime(selectedPortfolioBalance, {
    enabled: !loadingReturnValuations && returnValuations.length === 0,
  });
  // Legacy series in the shape the period-return maths expects. It carries no per-month
  // return, so those fall back to Modified Dietz on value + net contributions.
  const legacyValuations = useMemo(
    () =>
      portfolioPerformanceOverTime.map((point) => ({
        date: point.date,
        currentHoldings: point.currentHoldings,
        netContributions: point.netContributions,
        netPosition: null,
        returnPct: null,
        oneMonthReturn: null,
      })),
    [portfolioPerformanceOverTime],
  );
  // Fetch market insights from PortalCurrentHoldings API in parallel with other calls
  const marketInsights = usePortalHoldings(selectedPortfolioId);
  // Note: performanceStorySeries and portfolioDrawdown are computed via useMemo below (after filterReturnValuationsPeriod)
  const [, setSelectedTabs] = useState<ISelectedTabs>({
    bidsAndOffers: { selectedId: 1 },
    recentTransactions: { selectedId: 1 },
  });
  const { userName, defaultCard, allValidCards } = useMetadata();
  const { grandTotal } = usePortfolioHoldingsTotals();
  const allPortfoliosBalance = portfolioBalances[0];
  const totalPortfolioValue =
    grandTotal + (allPortfoliosBalance?.balance ?? 0) + (allPortfoliosBalance?.balancePending ?? 0);
  const holdingsValueFormatted = gbpToTargetCurrency.convertAndFormat(totalPortfolioValue);

  // Local slideout state for top performers
  const [topPerformerSlideout, setTopPerformerSlideout] = useState<{
    isOpen: boolean;
    product: Product | null;
    lwin18: string | null; // Track selected wine for updating when data loads
  }>({
    isOpen: false,
    product: null,
    lwin18: null,
  });

  const handleTopPerformerClick = (lwin18: string) => {
    // Get data ONLY from PortalCurrentHoldings API via marketInsights (usePortalHoldings)
    const data = marketInsights.getHoldingInsight(lwin18);

    // If data not available yet, open slideout with loading state
    // The useEffect will update when data loads
    if (!data) {
      // Create minimal product for loading state
      const rawHolding = marketInsights.holdings.find((p) => p.lwin18 === lwin18);
      if (!rawHolding) return;

      setTopPerformerSlideout({
        isOpen: true,
        product: {
          id: rawHolding.lwin18,
          assetId: 0,
          lwin18: rawHolding.lwin18,
          wineName: rawHolding.wineName,
          vintage: `${Math.floor(rawHolding.vintage)}`,
          region: rawHolding.region,
          dealDate: '',
          dealRef: '',
          dealCCY: 'GBP',
          unit: rawHolding.unit,
          unitCount: 1,
          qty: rawHolding.qty,
          cultWinesAllocationRegion: rawHolding.cultWinesAllocationRegion,
          avgProfitAndLoss: 0,
          qtyForSale: 0,
          costPerUnit: 0,
          totalCost: 0,
          valuePerUnit: 0,
          totalValue: 0,
          changedPct: 0,
          totalMgmtFee: 0,
          totalCostWithMgmtFee: 0,
          netPosition: 0,
          profitAndLoss: 0,
          profitAndLossHistory: [],
          priceForSale: 0,
          latestDealDate: null,
          physicalQuantity: 0,
          highestBid: null,
          lowestOffer: null,
        },
        lwin18,
      });
      return;
    }

    // Convert PortalHolding to Product with all quality metrics
    const product: Product = {
      // Base fields
      id: data.lwin18,
      assetId: 0,
      lwin18: data.lwin18,
      wineName: data.wineName,
      vintage: `${Math.floor(data.vintage)}`,
      region: data.region,
      dealDate: data.marketPriceDate || '',
      dealRef: '',
      dealCCY: 'GBP',
      unit: data.unit,
      unitCount: 1,
      qty: data.qty,
      cultWinesAllocationRegion: data.cultWinesAllocationRegion,

      // Financial fields
      avgProfitAndLoss: data.pnl,
      qtyForSale: 0,
      costPerUnit: data.totalCost / data.qty,
      totalCost: data.totalCost,
      valuePerUnit: data.marketPrice,
      totalValue: data.totalValue,
      changedPct: data.pnlPercent * 100,
      totalMgmtFee: 0,
      totalCostWithMgmtFee: data.totalCost,
      netPosition: data.pnl,
      profitAndLoss: data.pnl,
      profitAndLossHistory: [],
      priceForSale: 0,
      latestDealDate: null,
      physicalQuantity: data.qty,

      // Trading data
      highestBid: data.highestBid
        ? {
            price: data.highestBid.price,
            priceGbp: data.highestBid.price,
            priceInTargetCurrency: data.highestBid.price,
            orderType: null,
            orderCreatedDatetime: null,
            quantity: data.highestBid.quantityOutstanding,
            quantityOutstanding: data.highestBid.quantityOutstanding,
          }
        : null,
      lowestOffer: data.lowestOffer
        ? {
            price: data.lowestOffer.price,
            priceGbp: data.lowestOffer.price,
            priceInTargetCurrency: data.lowestOffer.price,
            orderType: null,
            orderCreatedDatetime: null,
            quantity: data.lowestOffer.quantityOutstanding,
            quantityOutstanding: data.lowestOffer.quantityOutstanding,
          }
        : null,
      highestBidPrice: data.highestBidPrice,

      // Critic scores
      wa: data.waScore ?? data.wa,
      vinous: data.vScoreMedian?.toString() || data.vinous,
      combinedScore: data.normalizedCriticScore ?? data.waScore ?? data.vScoreMedian ?? undefined,

      // Wine Advocate Review
      wineAdvocateReview:
        data.waReviewer || data.waScore || data.wa
          ? {
              reviewer: data.waReviewer,
              scoreDate: data.waScoreDate,
              score: data.waScore,
              drinkYearMin: data.waDrinkYearMin,
              drinkYearMax: data.waDrinkYearMax,
              tastingNote: data.waTastingNote,
              articleUrl: data.waArticleUrl,
            }
          : undefined,

      // Vinous Review
      vinousReview:
        data.vReviewer || data.vScoreMedian || data.vinous
          ? {
              reviewer: data.vReviewer,
              reviewDate: data.vReviewDate,
              score: data.vScoreMedian,
              drinkFrom: data.vDrinkFrom,
              drinkTo: data.vDrinkTo,
              tastingNote: data.vTastingNote,
              externalLink: data.vExternalLink,
            }
          : undefined,

      // Quality metrics for Market Metrics section
      grade: data.grade,
      fairValueScore: data.fairValueScore,
      liquidityScore: data.liquidityScore,
      performanceScore: data.performanceScore,
      vintageScore: data.vintageScore,
      marketPrice: data.marketPrice,
    };

    setTopPerformerSlideout({
      isOpen: true,
      product,
      lwin18: data.lwin18,
    });
  };

  const closeTopPerformerSlideout = () => {
    setTopPerformerSlideout({
      isOpen: false,
      product: null,
      lwin18: null,
    });
  };

  // Update product with ALL data from PortalCurrentHoldings API when marketInsights loads
  useEffect(() => {
    if (topPerformerSlideout.isOpen && topPerformerSlideout.lwin18 && !marketInsights.isLoading) {
      const data = marketInsights.getHoldingInsight(topPerformerSlideout.lwin18);
      if (data) {
        // Build complete product from PortalCurrentHoldings data only
        const updatedProduct: Product = {
          // Base fields
          id: data.lwin18,
          assetId: 0,
          lwin18: data.lwin18,
          wineName: data.wineName,
          vintage: `${Math.floor(data.vintage)}`,
          region: data.region,
          dealDate: data.marketPriceDate || '',
          dealRef: '',
          dealCCY: 'GBP',
          unit: data.unit,
          unitCount: 1,
          qty: data.qty,
          cultWinesAllocationRegion: data.cultWinesAllocationRegion,

          // Financial fields
          avgProfitAndLoss: data.pnl,
          qtyForSale: 0,
          costPerUnit: data.totalCost / data.qty,
          totalCost: data.totalCost,
          valuePerUnit: data.marketPrice,
          totalValue: data.totalValue,
          changedPct: data.pnlPercent * 100,
          totalMgmtFee: 0,
          totalCostWithMgmtFee: data.totalCost,
          netPosition: data.pnl,
          profitAndLoss: data.pnl,
          profitAndLossHistory: [],
          priceForSale: 0,
          latestDealDate: null,
          physicalQuantity: data.qty,

          // Trading data
          highestBid: data.highestBid
            ? {
                price: data.highestBid.price,
                priceGbp: data.highestBid.price,
                priceInTargetCurrency: data.highestBid.price,
                orderType: null,
                orderCreatedDatetime: null,
                quantity: data.highestBid.quantityOutstanding,
                quantityOutstanding: data.highestBid.quantityOutstanding,
              }
            : null,
          lowestOffer: data.lowestOffer
            ? {
                price: data.lowestOffer.price,
                priceGbp: data.lowestOffer.price,
                priceInTargetCurrency: data.lowestOffer.price,
                orderType: null,
                orderCreatedDatetime: null,
                quantity: data.lowestOffer.quantityOutstanding,
                quantityOutstanding: data.lowestOffer.quantityOutstanding,
              }
            : null,
          highestBidPrice: data.highestBidPrice,

          // Critic scores
          wa: data.waScore ?? data.wa,
          vinous: data.vScoreMedian?.toString() || data.vinous,
          combinedScore: data.normalizedCriticScore ?? data.waScore ?? data.vScoreMedian ?? undefined,

          // Wine Advocate Review
          wineAdvocateReview:
            data.waReviewer || data.waScore || data.wa
              ? {
                  reviewer: data.waReviewer,
                  scoreDate: data.waScoreDate,
                  score: data.waScore,
                  drinkYearMin: data.waDrinkYearMin,
                  drinkYearMax: data.waDrinkYearMax,
                  tastingNote: data.waTastingNote,
                  articleUrl: data.waArticleUrl,
                }
              : undefined,

          // Vinous Review
          vinousReview:
            data.vReviewer || data.vScoreMedian || data.vinous
              ? {
                  reviewer: data.vReviewer,
                  reviewDate: data.vReviewDate,
                  score: data.vScoreMedian,
                  drinkFrom: data.vDrinkFrom,
                  drinkTo: data.vDrinkTo,
                  tastingNote: data.vTastingNote,
                  externalLink: data.vExternalLink,
                }
              : undefined,

          // Quality metrics for Market Metrics section
          grade: data.grade,
          fairValueScore: data.fairValueScore,
          liquidityScore: data.liquidityScore,
          performanceScore: data.performanceScore,
          vintageScore: data.vintageScore,
          marketPrice: data.marketPrice,
        };

        setTopPerformerSlideout((prev) => ({
          ...prev,
          product: updatedProduct,
        }));
      }
    }
  }, [marketInsights, topPerformerSlideout.isOpen, topPerformerSlideout.lwin18]);

  const showMessage = (message: string, messageType: string) => {
    enqueueSnackbar('', {
      autoHideDuration: 4000,
      content: () => {
        return (
          <div className={`text-sm rounded-md p-3 text-white ${messageType === 'success' ? 'bg-green' : 'bg-red'}`}>
            <span>{message}</span>
          </div>
        );
      },
    });
  };

  const onViewPortfolio = (viewId: ViewKeys, filterOption: string) => {
    if (onViewChange) onViewChange(viewId, filterOption);
  };

  const onEditCard = () => {
    openSlideout(SlideOutPanelViews.CUSTOM, {
      title: t('account:payments.manageCard.title'),
      customTemplate: () => (
        <EditCardHomePage
          cards={allValidCards}
          onClose={closeSlidout}
          onSuccess={(msg) => {
            showMessage(msg, 'success');
          }}
          onError={(msg) => {
            showMessage(msg, 'error');
          }}
        />
      ),
    });
  };
  const onAddCard = () => {
    openSlideout(SlideOutPanelViews.CUSTOM, {
      customTemplate: () => <CardDetailsForm amount={0} flow={FlowType.ADD_CARD} />,
      title: t('account:payments.addCard.title'),
    });
  };

  const onChartPeriodChange = (period: PerformancePeriodKey) => {
    setSelctedChartPeriod(period);
  };
  const onViewDetails = () => {
    onViewPortfolio(ViewKeys.DETIALS, selectedPortfolioBalance);
  };

  const onTabChange = (id: number, key: keyof ISelectedTabs) => {
    setSelectedTabs((prev) => ({ ...prev, [key]: { selectedId: id } }));
  };

  const selectedPortfolioInfo = useMemo(() => {
    const baseInfo = getPortfolioInfo(portfolioBalances, displayText, selectedPortfolioBalance, seriesColors);

    // If return valuations summary is available, update the card values
    if (!loadingReturnValuations && returnValuations.length > 0) {
      // Get available balance and pending balance from portfolio balance
      const selectedBalance = portfolioBalances.find((x) => x.portfolioId === selectedPortfolioBalance);
      const availableBal = selectedBalance?.balance ?? 0;
      const pendingBal = selectedBalance?.balancePending ?? 0;

      // Current Assets = Portfolio Value + Available Balance + Pending Balance
      const currentAssets = returnValuationsSummary.portfolioValue + availableBal + pendingBal;

      // Update the info array with return valuations data
      const updatedInfo = baseInfo.info.map((item, index) => {
        switch (index) {
          case 0: // Current Assets
            return {
              ...item,
              body: (className = 'text-base') => (
                <span className={`${className} ${currentAssets > 0 ? 'text-[#68CA5D]' : ''}`}>
                  {currentAssets > 0 ? '+' : ''}
                  {formatterGbp.format(currentAssets)}
                </span>
              ),
              dropdownItems: [
                {
                  id: '1',
                  value: formatterGbp.format(returnValuationsSummary.portfolioValue),
                  content: (
                    <div className="py-3 border-b border-b-[#FF906D] dark:border-b-teal-400 flex flex-row items-center gap-1">
                      <span className="font-bold text-gray-800 dark:text-gray-200">Portfolio Value:</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatterGbp.format(returnValuationsSummary.portfolioValue)}
                      </span>
                    </div>
                  ),
                },
                {
                  id: '2',
                  value: formatterGbp.format(availableBal),
                  content: (
                    <div className="py-3 border-b border-b-[#FF906D] dark:border-b-teal-400 flex flex-row items-center gap-1">
                      <span className="font-bold text-gray-800 dark:text-gray-200">Available Balance:</span>
                      <span className="text-gray-700 dark:text-gray-300">{formatterGbp.format(availableBal)}</span>
                    </div>
                  ),
                },
                {
                  id: '3',
                  value: formatterGbp.format(pendingBal),
                  content: (
                    <div className="py-3 border-b border-b-[#FF906D] dark:border-b-teal-400 flex flex-row items-center gap-1">
                      <span className="font-bold text-gray-800 dark:text-gray-200">Pending Balance:</span>
                      <span className="text-gray-700 dark:text-gray-300">{formatterGbp.format(pendingBal)}</span>
                    </div>
                  ),
                },
              ],
            };
          case 1: // Inflows
            return {
              ...item,
              body: (className = 'text-base') => (
                <span className={className}>{formatterGbp.format(returnValuationsSummary.inflows)}</span>
              ),
            };
          case 2: // Outflows
            return {
              ...item,
              body: (className = 'text-base') => (
                <span className={className}>{formatterGbp.format(returnValuationsSummary.outflows)}</span>
              ),
              dropdownItems: [
                {
                  id: '1',
                  value: formatterGbp.format(returnValuationsSummary.refund),
                  content: (
                    <div className="py-3 border-b border-b-[#FF906D] dark:border-b-teal-400 flex flex-row items-center gap-1">
                      <span className="font-bold text-gray-800 dark:text-gray-200">Refunds:</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatterGbp.format(returnValuationsSummary.refund)}
                      </span>
                    </div>
                  ),
                },
                {
                  id: '2',
                  value: formatterGbp.format(returnValuationsSummary.transferOut),
                  content: (
                    <div className="py-3 border-b border-b-[#FF906D] dark:border-b-teal-400 flex flex-row items-center gap-1">
                      <span className="font-bold text-gray-800 dark:text-gray-200">Transfers Out:</span>
                      <span className="text-gray-700 dark:text-gray-300">
                        {formatterGbp.format(returnValuationsSummary.transferOut)}
                      </span>
                    </div>
                  ),
                },
              ],
            };
          case 3: // Net Position
            return {
              ...item,
              body: (className = 'text-base') => (
                <span className={className}>{formatterGbp.format(returnValuationsSummary.netPosition)}</span>
              ),
            };
          default:
            return item;
        }
      });

      return { ...baseInfo, info: updatedInfo };
    }

    return baseInfo;
  }, [
    portfolioBalances,
    selectedPortfolioBalance,
    displayText,
    loadingReturnValuations,
    returnValuations,
    returnValuationsSummary,
  ]);

  // Sync selectedPortfolioBalance with global selectedPortfolioId or currentPortfolio prop
  useEffect(() => {
    // Use global selectedPortfolioId if available, otherwise fall back to currentPortfolio prop
    const portfolioId = selectedPortfolioId ? String(selectedPortfolioId) : currentPortfolio;
    setSelectedPortfolioBalance(portfolioId);
  }, [selectedPortfolioId, currentPortfolio]);

  // Helper to filter return valuations by period
  const filterReturnValuationsPeriod = (
    data: ReturnValuationPerformance[],
    periodInMonths: number,
  ): ReturnValuationPerformance[] => {
    if (periodInMonths === -1) return data;
    const today = moment();
    const endDate = today.subtract(1, 'months').endOf('month');
    const startDate =
      periodInMonths === 0
        ? moment(`${today.year() - 1}-12-31`)
        : moment(endDate).subtract(periodInMonths, 'months').endOf('month');

    return data.filter(
      (x) => moment(x.date, 'YYYY-MM-DD').isBetween(startDate, endDate) || moment(x.date, 'YYYY-MM-DD').isSame(endDate),
    );
  };

  // Compute chart series synchronously via useMemo (not useEffect) so data is available
  // in the same render cycle that the loading condition switches to false
  const { performanceStorySeries, portfolioDrawdown } = useMemo(() => {
    const periodInMonths = periodToMonthsMap[selectedChartPeriod];

    // Use return valuations data if available
    if (returnValuations && returnValuations.length > 0) {
      const filteredData = filterReturnValuationsPeriod(returnValuations, periodInMonths);

      if (filteredData.length > 0) {
        // Calculate peak value and drawdown from return valuations
        let peakValue = 0;
        let maxDrawdownPct = 0;

        // Build NAV series data with enhanced tooltip info
        const navData = filteredData.map((item) => {
          const nav = item.currentHoldings; // This is from the 'nav' field
          const netCapital = item.netContributions; // cumNetCapitalInvested
          const netPosition = item.netPosition ?? nav - netCapital;
          // Use API-provided return percentage or calculate it
          const returnPct = item.returnPct ?? (netCapital > 0 ? ((nav - netCapital) / netCapital) * 100 : 0);

          // Track peak and calculate drawdown
          if (nav > peakValue) {
            peakValue = nav;
          }
          const drawdownFromPeak = peakValue > 0 ? peakValue - nav : 0;
          const drawdownPct = peakValue > 0 ? ((peakValue - nav) / peakValue) * 100 : 0;
          if (drawdownPct > maxDrawdownPct) {
            maxDrawdownPct = drawdownPct;
          }

          return {
            x: new Date(item.date).getTime(),
            y: nav,
            netCapital,
            netPosition,
            returnPct,
            drawdown: drawdownFromPeak,
          };
        });

        // Build Cumulative Net Capital Invested series data
        const netCapitalData = filteredData.map((item) => ({
          x: new Date(item.date).getTime(),
          y: item.netContributions,
        }));

        // Build Net Position series data (for bar chart)
        const netPositionData = filteredData.map((item) => ({
          x: new Date(item.date).getTime(),
          y: item.netPosition ?? 0,
        }));

        return {
          performanceStorySeries: [
            {
              id: 'netPosition',
              name: 'Net Position',
              color: '#D97B4A', // Orange color matching the reference image
              type: 'column' as const,
              data: netPositionData,
            },
            {
              id: 'netCapital',
              name: 'Cumulative Net Capital Invested',
              color: isDark ? '#4ADE80' : '#2E7D32', // Brighter green on dark mode
              type: 'line' as const,
              data: netCapitalData,
            },
            {
              id: 'nav',
              name: 'NAV',
              color: isDark ? '#60A5FA' : '#1E3A8A', // Brighter blue on dark mode
              type: 'line' as const,
              data: navData,
            },
          ] as PerformanceStorySeries[],
          portfolioDrawdown: maxDrawdownPct,
        };
      }
    }

    // Fallback to legacy portfolioPerformanceOverTime data
    const filteredData = filterPeriod(portfolioPerformanceOverTime, periodInMonths);
    const chartData = getHoldingsVsInvestedChartDatasource(filteredData);

    // Create performance story series data with enhanced tooltip data
    if (chartData.currentHoldings.length > 0) {
      // Calculate peak value and drawdown
      let peakValue = 0;
      let maxDrawdownPct = 0;
      const enhancedData = chartData.currentHoldings.map((point, idx) => {
        const netCapitalPoint = chartData.netContributions[idx];
        const nav = point.y;
        const netCapital = netCapitalPoint?.y || 0;
        const netPosition = nav - netCapital;
        const returnPct = netCapital > 0 ? ((nav - netCapital) / netCapital) * 100 : 0;

        // Track peak and calculate drawdown
        if (nav > peakValue) {
          peakValue = nav;
        }
        const drawdownFromPeak = peakValue > 0 ? peakValue - nav : 0;
        const drawdownPct = peakValue > 0 ? ((peakValue - nav) / peakValue) * 100 : 0;
        if (drawdownPct > maxDrawdownPct) {
          maxDrawdownPct = drawdownPct;
        }

        return {
          x: point.x,
          y: nav,
          netCapital,
          netPosition,
          returnPct,
          drawdown: drawdownFromPeak,
        };
      });

      return {
        performanceStorySeries: [
          {
            id: 'nav',
            name: 'NAV',
            color: '#8B7CE8',
            data: enhancedData,
          },
        ] as PerformanceStorySeries[],
        portfolioDrawdown: maxDrawdownPct,
      };
    }

    return { performanceStorySeries: [] as PerformanceStorySeries[], portfolioDrawdown: undefined };
  }, [selectedChartPeriod, returnValuations, portfolioPerformanceOverTime, isDark]);

  return (
    <div className="w-full h-full px-5 flex flex-col transition-colors" style={{ color: accountThemeColors.text }}>
      <div className="flex flex-col sm:flex-row justify-between items-stretch mt-5 mb-6 gap-4">
        {/* Account Summary Pane */}
        <div
          className="flex-1 flex flex-col rounded-lg transition-colors"
          style={{
            backgroundColor: accountThemeColors.background,
            border: `1px solid ${accountThemeColors.border}`,
          }}
        >
          {/* Header */}
          <div
            className="flex justify-between items-center px-4 py-3"
            style={{ borderBottom: `1px solid ${accountThemeColors.border}` }}
          >
            <span className="text-base sm:text-lg">{displayText[DisplayTextKeys.TEXT_PORTFOLIO_BALANCE]}</span>
            <button
              className="text-sm font-medium transition-colors hover:opacity-80"
              style={{ color: accountThemeColors.accent }}
              onClick={() => {
                navigate(NavigationPath.MY_PORTFOLIO);
              }}
            >
              {displayText[DisplayTextKeys.TEXT_VIEW_DETAILS]}
            </button>
          </div>

          {/* Content */}
          <div className="flex flex-col p-4 flex-1">
            {loadingBalances ? (
              <AccountSummarySkeleton />
            ) : (
              <>
                {/* Welcome Section */}
                <div className="rounded-lg px-5 py-4 mb-2" style={{ backgroundColor: accountThemeColors.accentLight }}>
                  <div className="flex flex-col">
                    <span className="text-base sm:text-[28px]" style={{ color: accountThemeColors.text }}>
                      {`${displayText[DisplayTextKeys.WELCOME_TITLE]} ${userName}`}
                    </span>
                    {managementFeeUpdates14758 && (
                      <div className="flex items-baseline flex-wrap gap-x-3 mt-1">
                        <span className="text-sm" style={{ color: accountThemeColors.textSecondary }}>
                          {displayText[DisplayTextKeys.WELCOME_SUBTITLE]}
                        </span>
                        <span className="text-3xl sm:text-4xl font-bold" style={{ color: accountThemeColors.accent }}>
                          {holdingsValueFormatted}
                        </span>
                        {/* Total P&L indicator - hidden for now
                        {!loadingTotalInvested && !marketInsights.isLoading && marketInsights.holdings.length > 0 && (() => {
                          const totalPnL = marketInsights.holdings.reduce((sum, h) => sum + (h.pnl || 0), 0);
                          if (totalPnL === 0) return null;
                          return (
                            <span
                              className="text-sm font-medium"
                              style={{
                                color: totalPnL >= 0 ? '#10B981' : '#EF4444',
                              }}
                            >
                              {totalPnL >= 0 ? '▲' : '▼'}{' '}
                              {totalPnL >= 0 ? '+' : ''}
                              {formatterGbp.format(totalPnL)}
                            </span>
                          );
                        })()} */}
                      </div>
                    )}
                  </div>
                </div>

                {/* Account Overview */}
                <div className="mt-1">
                  <AccountOverview
                    holdings={marketInsights.holdings}
                    isLoadingHoldings={marketInsights.isLoading}
                    cashBalance={
                      portfolioBalances.find((x) => x.portfolioId === selectedPortfolioBalance)?.balance ?? 0
                    }
                    balancePending={
                      portfolioBalances.find((x) => x.portfolioId === selectedPortfolioBalance)?.balancePending ?? 0
                    }
                    inflows={returnValuationsSummary.inflows}
                    outflows={returnValuationsSummary.outflows}
                    netPosition={returnValuationsSummary.netPosition}
                    netPositionPct={returnValuationsSummary.netPositionPct}
                    netCapitalInvested={returnValuationsSummary.netCapitalInvested}
                    portfolioValue={returnValuationsSummary.portfolioValue}
                    lastValuationDate={
                      returnValuations.length > 0 ? returnValuations[returnValuations.length - 1].date : undefined
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Saved Cards Pane */}
        <div
          className="hidden sm:flex w-[300px] flex-col rounded-lg overflow-hidden transition-colors"
          style={{
            backgroundColor: accountThemeColors.background,
            border: `1px solid ${accountThemeColors.border}`,
          }}
        >
          {/* Header */}
          <div
            className="flex justify-between items-center px-4 py-3"
            style={{ borderBottom: `1px solid ${accountThemeColors.border}` }}
          >
            <span className="text-base sm:text-lg">{displayText[DisplayTextKeys.SAVE_CARDS]}</span>
            <button
              className="text-sm font-medium transition-colors hover:opacity-80"
              style={{ color: accountThemeColors.accent }}
              onClick={() => navigate(NavigationPath.ACCOUNTS_PAYMENTS)}
            >
              {displayText[DisplayTextKeys.VIEW_SETTINGS]}
            </button>
          </div>

          {/* Card Display */}
          <div className="flex flex-col p-4 flex-1">
            <div
              className="rounded-lg p-4 flex flex-col"
              style={{
                background: isDark
                  ? 'linear-gradient(135deg, #1E3A50 0%, #0F1E32 100%)'
                  : 'linear-gradient(135deg, #A1A3F9 0%, #8B7CE8 100%)',
              }}
            >
              <div className="flex items-center justify-between mb-3">
                {selectImage(defaultCard?.scheme)}
                <PayPassIcon className="w-5 h-5 text-white opacity-80" />
              </div>
              <span className="text-[10px] font-medium tracking-wider opacity-70 mb-1" style={{ color: '#FFFFFF' }}>
                DEFAULT CARD
              </span>
              <span className="text-sm font-medium text-white">{defaultCard?.name}</span>
              <span className="text-sm text-white opacity-90 mt-1">{`•••• •••• •••• ${defaultCard?.last4}`}</span>
              <span className="text-xs text-white opacity-70 mt-1">{defaultCard?.expiryDate}</span>
            </div>

            {/* Card Actions */}
            <div className="flex flex-row justify-between items-center mt-4">
              <button
                className="flex flex-row items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: accountThemeColors.cardBackground,
                  border: `1px solid ${accountThemeColors.border}`,
                  color: accountThemeColors.text,
                }}
                onClick={onAddCard}
              >
                <AddCardIcon className="w-4 h-4" /> Add Card
              </button>
              {allValidCards.length > 1 && (
                <button
                  className="text-sm font-medium transition-colors hover:opacity-80"
                  style={{ color: accountThemeColors.accent }}
                  onClick={onEditCard}
                >
                  Edit Card
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="px-3 pb-3 flex justify-center">
        <span className="text-xs text-center max-w-3xl" style={{ color: accountThemeColors.textSecondary }}>
          The historical performance chart is calculated in GBP, which is the base currency for the fine wine market.
          You can view your portfolio details and cash balance in the currency of your choice by updating the
          preferences in your account section under settings.
        </span>
      </div>

      <div className="flex flex-col mt-6 mb-8 w-full py-3">
        {/* Graph - only show skeleton on first load, keep previous chart visible during refetch */}
        {loadingBalances ||
        (loadingReturnValuations && returnValuations.length === 0 && !portfolioPerformanceOverTime.length) ? (
          <ChartSkeleton />
        ) : (
          <div className="flex-1 flex flex-col rounded-md shadow-md overflow-hidden">
            <PerformanceStoryChart
              series={performanceStorySeries}
              drawdownPct={portfolioDrawdown}
              selectedPeriod={selectedChartPeriod}
              onPeriodChange={onChartPeriodChange}
            />
          </div>
        )}
      </div>

      {/* <div className="flex flex-col mt-[10px]  w-full sm:flex-row py-3 gap-3    ">
        <div className="bg-gray-50 min-w-[417px] hidden sm:flex flex-col  flex-1 rounded-md shadow-md p-[16px] ">
          {activeOffers.isLoading ? (
            <Loading />
          ) : (
            <>
              <div className="flex justify-between">
                <span className="text-base">Your Active Offers</span>
                <div className="flex flex-row gap-5">
                  <TabButtons
                    tabs={tabs.bidsAndOffers}
                    selectedId={selectedTabs.bidsAndOffers.selectedId}
                    onClick={(id) => onTabChange(id, 'bidsAndOffers')}
                  />
                  <Button
                    isLink={true}
                    className="text-sm"
                    onClick={() => {
                      navigate(NavigationPath.TRADE, {
                        state: {
                          initSubTab: SUB_TABS_ENUMS.MY_OFFERS,
                        },
                      });
                    }}
                  >
                    {selectedTabs.bidsAndOffers.selectedId === 1 ? 'View Offers' : 'View Bids'}
                  </Button>
                </div>
              </div>
              <div className=" flex flex-col  mt-[16px] gap-[6px]">
                {activeOffers &&
                  activeOffers.data.length > 0 &&
                  activeOffers.data
                    .filter((x) => {
                      if (selectedTabs.bidsAndOffers.selectedId === 1) return x.type === BIDS_OR_OFFERS_ENUM.OFFERS;
                      return x.type === BIDS_OR_OFFERS_ENUM.BIDS;
                    })
                    .map((listing, index: number) => {
                      return (
                        <WineAssetCard
                          key={index}
                          wineAsset={{
                            imageUrl: listing.imageUrl,
                            wineName: listing.wineName,
                            color: listing.color,
                            rightValues: {
                              type: 'recentTranactions',
                              values: {
                                qtyAndUnitSize: listing.unitsize,
                                total: listing.price,
                              },
                            },
                          }}
                          index={index}
                        />
                      );
                    })}
              </div>
            </>
          )}
        </div>

        <div className="bg-gray-50 min-w-[417px] hidden sm:flex flex-col flex-1  rounded-md shadow-md p-[16px] ">
          {recentTransactions.isLoading ? (
            <Loading />
          ) : (
            <>
              <div className="flex justify-between">
                <span className="text-base">Recent Tranactions</span>

                <div className="flex flex-row gap-5">
                  <TabButtons
                    tabs={tabs.recentTransactions}
                    selectedId={selectedTabs.recentTransactions.selectedId}
                    onClick={(id) => onTabChange(id, 'recentTransactions')}
                  />
                  <Button
                    isLink={true}
                    className="text-sm"
                    onClick={() => {
                      navigate(NavigationPath.TRADE, {
                        state: {
                          initTabIndex: 1,
                          initSubTab:
                            selectedTabs.recentTransactions.selectedId === 1
                              ? TRANSACTION_TYPE_ENUM.BUY
                              : TRANSACTION_TYPE_ENUM.SELL,
                          initTab: TabTypes.TRADE_HISTORY,
                        },
                      });
                    }}
                  >
                    View All
                  </Button>
                </div>
              </div>
              <div className=" flex flex-col  mt-[16px] gap-[6px]">
                {recentTransactions &&
                  recentTransactions.data.length > 0 &&
                  recentTransactions.data
                    .filter((x) => {
                      if (selectedTabs.recentTransactions.selectedId === 1)
                        return x.transactionType === TRANSACTION_TYPE_ENUM.BUY;
                      return x.transactionType === TRANSACTION_TYPE_ENUM.SELL;
                    })
                    .map((listing, index: number) => {
                      return (
                        <WineAssetCard
                          key={index}
                          wineAsset={{
                            imageUrl: listing.imageUrl,
                            wineName: listing.wineName,
                            color: listing.color,
                            rightValues: {
                              type: 'recentTranactions',
                              values: {
                                qtyAndUnitSize: listing.unitsize,
                                total: listing.total,
                              },
                            },
                          }}
                          index={index}
                        />
                      );
                    })}
              </div>
            </>
          )}
        </div>
      </div> */}

      {/* Regional Allocation & Portfolio Performance */}
      <div className="w-full mb-16">
        <RegionalValuePerformance
          holdings={marketInsights.holdings}
          valuations={returnValuations.length > 0 ? returnValuations : legacyValuations}
          isLoading={marketInsights.isLoading}
          isValuationsLoading={loadingReturnValuations}
        />
      </div>

      {/* Top & Bottom Performers */}
      <div className="w-full mb-8">
        <TopBottomPerformers
          holdings={marketInsights.holdings}
          isLoading={marketInsights.isLoading}
          onItemClick={handleTopPerformerClick}
        />
      </div>

      <div className="w-full mb-8 flex flex-col lg:flex-row gap-8 lg:gap-4">
        <div className="w-full lg:w-[60%]">
          <PerformanceMomentum holdings={marketInsights.holdings} isLoading={marketInsights.isLoading} />
        </div>
        <div className="w-full lg:w-[40%]">
          <PortfolioQuickStats holdings={marketInsights.holdings} isLoading={marketInsights.isLoading} />
        </div>
      </div>

      {/* {deployAnnualizedReturnFeature && (
          <div className="flex-1 flex flex-col p-3 h-full  ">
            <AnnualisedReturn selectedPortfolioId={selectedPortfolioBalance} />
          </div>
        )} */}

      {/* <div className="flex flex-1 flex-col sm:flex-row gap-3  h-full w-full my-5 ">
        <div className="flex-1 flex-col rounded-md bg-gray-50 pb-5 shadow-md p-[16px]">
          <div className="flex justify-between">
            <span className="text-base"> Our Investment Picks</span>
            <Button isLink={true} className="text-sm" onClick={() => navigate(NavigationPath.ACCOUNTS_PAYMENTS)}>
              View Offers
            </Button>
          </div>
          <div>INVESMENT OFFERS</div>
        </div>

        <div className=" flex-1 flex-col rounded-md bg-gray-50 pb-5 shadow-md p-[16px]">
          <div className="flex justify-between">
            <span className="text-base"> Your Wine Stats</span>
            <Button isLink={true} className="text-sm" onClick={() => navigate(NavigationPath.MY_CELLAR)}>
              View Cellar
            </Button>
          </div>
          <div>CELLAR</div>
        </div>

        <div className="flex flex-1 flex-col justify-between gap-3 ">
          <div className="flex-1  flex-col rounded-md bg-gray-50 shadow-md">
            <div className="w-full bg-gray-100  text-left rounded-md p-5 gap-2 ">
              <img
                src={cutlWinesLoversImage}
                className="absolute left-[172px] bg-transparent  w-[175px] object-cover"
                alt="wine lovers"
              />
              <div className="text-base font-medium ">{displayText[DisplayTextKeys.ARRANGE_DELIVERY_TITLE]}</div>
              <div className="text-sm w-[233px]">{displayText[DisplayTextKeys.ARRANGE_DELIVERY_SUBTITLE]}</div>
              <Button
                className="btn text-14 font-normal bg-orange rounded-full mt-3  text-black"
                onClick={() => {
                  alert('Arrange Delivery Clicked');
                }}
                props={{
                  name: DisplayTextKeys.ARRANGE_DELIVERY_BUTTON_TEXT,
                }}
              >
                {displayText[DisplayTextKeys.ARRANGE_DELIVERY_BUTTON_TEXT]}
              </Button>
            </div>
          </div>
          <div className="flex-1  rounded-md bg-gray-50  shadow-md ">
            <div className="w-full bg-gray-100  text-left rounded-md p-5 gap-2 ">
              <img
                src={cutlWinesLoversImage}
                className="absolute left-[172px] bg-transparent  w-[175px] object-cover"
                alt="wine lovers"
              />
              <div className="text-base font-medium ">{displayText[DisplayTextKeys.ARRANGE_DELIVERY_TITLE]}</div>
              <div className="text-sm w-[233px]">{displayText[DisplayTextKeys.ARRANGE_DELIVERY_SUBTITLE]}</div>
              <Button
                className="btn text-14 font-normal bg-orange rounded-full mt-3  text-black"
                onClick={() => {
                  alert('Arrange Delivery Clicked');
                }}
                props={{
                  name: DisplayTextKeys.ARRANGE_DELIVERY_BUTTON_TEXT,
                }}
              >
                {displayText[DisplayTextKeys.ARRANGE_DELIVERY_BUTTON_TEXT]}
              </Button>
            </div>
          </div>
          <div className="flex-1 flex-col rounded-md bg-gray-50  shadow-md">
            <div className="w-full bg-gray-100  text-left rounded-md p-5 gap-2 ">
              <img
                src={cutlWinesLoversImage}
                className="absolute left-[172px] bg-transparent  w-[175px] object-cover"
                alt="wine lovers"
              />
              <div className="text-base font-medium ">{displayText[DisplayTextKeys.ARRANGE_DELIVERY_TITLE]}</div>
              <div className="text-sm w-[233px]">{displayText[DisplayTextKeys.ARRANGE_DELIVERY_SUBTITLE]}</div>
              <Button
                className="btn text-14 font-normal bg-orange rounded-full mt-3  text-black"
                onClick={() => {
                  alert('Arrange Delivery Clicked');
                }}
                props={{
                  name: DisplayTextKeys.ARRANGE_DELIVERY_BUTTON_TEXT,
                }}
              >
                {displayText[DisplayTextKeys.ARRANGE_DELIVERY_BUTTON_TEXT]}
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-3">
          <div className="flex-1 flex flex-col rounded-md bg-gray-50 pb-5 shadow-md p-[16px]">
            <div className="flex justify-between">
              <span className="text-base"> Notification Preferences</span>
              <Button isLink={true} className="text-sm" onClick={() => navigate(NavigationPath.ACCOUNTS_HELP_CENTER)}>
                Help Center
              </Button>
            </div>
            <div>Notification PREF</div>
          </div>

          <div className="flex-1 flex flex-col rounded-md bg-gray-50 pb-5 shadow-md p-[16px]">
            <div className="flex justify-between">
              <span className="text-base"> Marketing Preferences</span>
              <Button isLink={true} className="text-sm" onClick={() => navigate(NavigationPath.ACCOUNTS_HELP_CENTER)}>
                Help Center
              </Button>
            </div>
            <div>MARKETING PREF</div>
          </div>
        </div>
      </div> */}

      <div className="h-10" />

      {/* Top Performer Slideout */}
      <SlideOutPanel
        isOpen={topPerformerSlideout.isOpen}
        onClose={closeTopPerformerSlideout}
        title={t('portfolio:wineDetails.title')}
        headClassName="bg-gray-100 dark:bg-slate-800 text-black dark:text-white"
      >
        {topPerformerSlideout.isOpen && topPerformerSlideout.product && (
          <ProductSlideout
            product={topPerformerSlideout.product}
            timestamp={Date.now()}
            onClose={closeTopPerformerSlideout}
            isMetricsLoading={marketInsights.isLoading}
          />
        )}
      </SlideOutPanel>
    </div>
  );
};
